import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { getCookie } from '../lib/cookie-utils';
import type {
  ApiResponse,
  GuideApiResponse,
  StateApiResponse,
  BillingLinkResponse,
  BrowserLoginTokenResponse,
  ConfluenceSpace,
  ConfluenceConnection,
  PortalUpdateRequest,
  GuideSettingsUpdateRequest,
  UserProperties,
  UserAccount,
  ArticleApiResponse,
  CreateArticleRequest,
  UpdateArticleRequest,
  ArticleSettings,
  Guide,
  HelpCenterApiResponse,
  HelpCentersApiResponse,
  CreateHelpCenterRequest,
  UpdateHelpCenterRequest,
  HelpCenterCategory,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CategoryApiResponse,
  CategoriesApiResponse,
  ReorderCategoriesRequest,
  HelpCenterItem,
  CreateItemRequest,
  UpdateItemRequest,
  ItemApiResponse,
  ItemsApiResponse,
  ReorderItemsRequest,
  HelpCenterStructureNode,
  RevisionsListResponse,
  RevisionResponse,
  Asset,
  AssetsApiResponse,
  CreateAssetFromUrlRequest,
  CustomDomainResponse,
  CustomDomainStatusResponse,
  CustomDomainVerifyResponse,
  LicenseManagerDashboard
} from '../types';

// Environment configuration
const IS_DEV = process.env.NODE_ENV === 'development';
const API_BASE_URL = IS_DEV ? 'http://localhost:3000' : 'https://tower.folge.me';

class ApiClient {
  private instance: AxiosInstance | null = null;
  private token: string | null = null;
  private useCookies: boolean = false;

  /**
   * Get token from localStorage or cookies
   * Priority: 1) Instance token, 2) localStorage, 3) cookies
   */
  private getToken(): string | null {
    // First, use the token stored in this instance
    if (this.token) {
      return this.token;
    }
    
    // Try localStorage
    try {
      const storedToken = localStorage.getItem('folge-session-token');
      if (storedToken) {
        return storedToken;
      }
    } catch {
      // localStorage not available (SSR)
    }
    
    // Fallback to cookie
    const cookieToken = getCookie('session-token');
    if (cookieToken) {
      return cookieToken;
    }
    
    return null;
  }

  /**
   * Initialize the API client with authentication token
   */
  createApi(token?: string, useCookies: boolean = false): void {
    this.token = token || null;
    this.useCookies = useCookies;
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // 30 second timeout
      withCredentials: useCookies, // Enable cookies for cookie-based requests
    });

    // Add request interceptor for authentication
    this.instance.interceptors.request.use(
      (config) => {
        // Always try to get token from instance, localStorage, or cookies
        const token = this.getToken();
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error);
        // Handle 401 unauthorized - redirect to login if using cookies
        if (error.response?.status === 401 && useCookies) {
          // Clear any stored token
          this.token = null;
          // Redirect will be handled by the app
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Check if API client is initialized
   */
  private ensureInitialized(): void {
    if (!this.instance) {
      throw new Error('API client not initialized. Call createApi() first.');
    }
  }

  /**
   * Make authenticated API request
   */
  private async request<T>(config: Record<string, unknown>): Promise<AxiosResponse<T>> {
    this.ensureInitialized();
    return this.instance!.request<T>(config);
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<{
    ok: boolean;
    token?: string;
    me?: UserAccount;
    error?: string;
  }> {
    // Use a temporary instance for login (no auth required)
    const tempInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true, // Enable cookies for session
    });

    try {
      const response = await tempInstance.post('/login', {
        email,
        password,
        source: 'web'
      });

      // The server returns {ok: true, me: ..., token: ...} on success
      // or {ok: false, errors: [...]} on error
      if (response.data) {
        if (response.data.ok && response.data.me && response.data.token) {
          return {
            ok: true,
            token: response.data.token,
            me: response.data.me
          };
        } else if (response.data.ok === false) {
          // Handle error response
          const errors = response.data.errors || [];
          return {
            ok: false,
            error: errors.length > 0 ? errors[0] : 'Login failed'
          };
        }
      }
      
      return {
        ok: false,
        error: 'Invalid response from server'
      };
    } catch (error: any) {
      // Handle error response
      // Server returns {ok: false, errors: ["invalid_credentials"]}
      const errorData = error.response?.data;
      let errorMessage = 'Invalid email or password';
      
      if (errorData) {
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMessage = errorData.errors[0];
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }
      
      return {
        ok: false,
        error: errorMessage
      };
    }
  }

  /**
   * Check if user is authenticated via session cookie
   * This uses a temporary axios instance, but always includes Bearer token if available
   */
  async checkSession(): Promise<{
    ok: boolean;
    me?: UserAccount;
    token?: string;
    properties?: UserProperties;
    error?: string;
  }> {
    // Get token from localStorage or cookie
    const token = this.getToken();
    
    // Create a temporary axios instance for the check
    // This does NOT modify the main instance, so the token stays intact
    const tempInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true, // Enable cookies for cookie-based requests
    });
    
    // Add request interceptor to always include Bearer token if available
    tempInstance.interceptors.request.use((config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    
    try {
      // Make request with Bearer token (if available) and cookies
      const response = await tempInstance.post<StateApiResponse>('/web-api/state');
      const stateResponse = response.data;
      
      if (stateResponse.ok && stateResponse.me && stateResponse.properties) {
        return {
          ok: true,
          me: stateResponse.me,
          token: stateResponse.token || token || undefined,
          properties: stateResponse.properties
        };
      }
      
      return {
        ok: false,
        error: 'Not authenticated'
      };
    } catch (error: any) {
      // If 401, user is not authenticated
      if (error.response?.status === 401) {
        return {
          ok: false,
          error: 'Not authenticated'
        };
      }
      
      return {
        ok: false,
        error: error.response?.data?.error || 'Session check failed'
      };
    }
  }

  /**
   * Get user state and properties
   */
  async getState(): Promise<StateApiResponse> {
    const response = await this.request<StateApiResponse>({
      url: '/web-api/state',
      method: 'POST'
    });

    return response.data;
  }

  /**
   * Get guide by ID
   */
  async getGuide(guideId: string): Promise<GuideApiResponse> {
    const response = await this.request<GuideApiResponse>({
      url: `/web-api/get-guide/${guideId}`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Delete guide by ID
   */
  async deleteGuide(guideId: string): Promise<ApiResponse> {
    const response = await this.request<ApiResponse>({
      url: `/web-api/delete-guide/${guideId}`,
      method: 'POST'
    });
    return response.data;
  }

  /**
   * Update guide settings
   */
  async updateGuideSettings(guideId: string, settings: GuideSettingsUpdateRequest['settings']): Promise<GuideApiResponse> {
    const response = await this.request<GuideApiResponse>({
      url: `/web-api/update-settings/${guideId}`,
      method: 'POST',
      data: { settings }
    });
    return response.data;
  }

  /**
   * Update portal settings
   */
  async updatePortalSettings(params: PortalUpdateRequest): Promise<StateApiResponse> {
    const formData = new FormData();
    formData.append('settings', JSON.stringify(params.settings));
    
    if (params.removeFile) {
      formData.append('removeFile', 'true');
    }

    if (params.logoFile) {
      formData.append('file', params.logoFile);
    }

    const response = await this.request<StateApiResponse>({
      url: '/web-api/update-portal/',
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * Get Confluence spaces
   */
  async getConfluenceSpaces(connection: ConfluenceConnection): Promise<ApiResponse & { spaces?: ConfluenceSpace[] }> {
    const response = await this.request<ApiResponse & { spaces?: ConfluenceSpace[] }>({
      url: '/web-api/confluence-get-spaces',
      method: 'POST',
      data: { connection }
    });
    return response.data;
  }

  /**
   * Generate billing link
   */
  async generateBillingLink(): Promise<BillingLinkResponse> {
    const response = await this.request<BillingLinkResponse>({
      url: '/web-api/billing',
      method: 'POST'
    });
    return response.data;
  }

  /**
   * Generate a temporary browser login token
   * Returns a token and URL that can be used to log in via browser
   */
  async generateBrowserLoginToken(): Promise<BrowserLoginTokenResponse> {
    const response = await this.request<BrowserLoginTokenResponse>({
      url: '/web-api/browser-login-token',
      method: 'POST'
    });
    return response.data;
  }

  /**
   * Request a password reset code (Step 1)
   * Sends email with reset code. Public endpoint - no auth required.
   */
  async requestResetCode(
    email: string,
    turnstileToken: string
  ): Promise<{ ok: boolean; success?: string; error?: string }> {
    const tempInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true,
    });

    try {
      const response = await tempInstance.post(
        '/license-manager/reset-password',
        new URLSearchParams({
          action: 'request_code',
          email,
          'cf-turnstile-response': turnstileToken,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
          },
        }
      );

      if (response.data?.ok && response.data?.success) {
        return { ok: true, success: response.data.success };
      }
      return {
        ok: false,
        error: response.data?.error || 'Request failed',
      };
    } catch (error: any) {
      const err = error.response?.data;
      return {
        ok: false,
        error: err?.error || error.message || 'Request failed',
      };
    }
  }

  /**
   * Set new password with reset code (Step 2)
   * Public endpoint - no auth required.
   */
  async setNewPassword(
    code: string,
    newPassword: string,
    turnstileToken: string
  ): Promise<{ ok: boolean; success?: string; error?: string }> {
    const tempInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true,
    });

    try {
      const response = await tempInstance.post(
        '/license-manager/reset-password',
        new URLSearchParams({
          action: 'set_new_password',
          code,
          newPassword,
          'cf-turnstile-response': turnstileToken,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
          },
        }
      );

      if (response.data?.ok && response.data?.success) {
        return { ok: true, success: response.data.success };
      }
      return {
        ok: false,
        error: response.data?.error || 'Update failed',
      };
    } catch (error: any) {
      const err = error.response?.data;
      return {
        ok: false,
        error: err?.error || error.message || 'Update failed',
      };
    }
  }

  /**
   * License Manager: get dashboard data (requires auth, admin).
   */
  async getLicenseManagerDashboard(): Promise<LicenseManagerDashboard> {
    this.ensureInitialized();
    const response = await this.instance!.get<LicenseManagerDashboard>('/license-manager', {
      headers: { Accept: 'application/json' },
    });
    return response.data;
  }

  /**
   * License Manager: logout (clears server session). Call useUserStore.getState().logout() and navigate to /login after.
   */
  async licenseManagerLogout(): Promise<void> {
    this.ensureInitialized();
    await this.instance!.get('/license-manager/logout', {
      maxRedirects: 0,
      validateStatus: (status) => status === 302 || status === 200,
    });
  }

  /**
   * License Manager: add_user, create_user, remove_user (returns JSON when returnJson=1).
   */
  async licenseManagerAction(params: {
    action: 'add_user' | 'create_user' | 'remove_user';
    email?: string;
    fullname?: string;
    password?: string;
    send_email?: string | number;
    /** Admin UI language for license / welcome emails (BCP-47 or base, e.g. en, de). */
    locale?: string;
  }): Promise<{
    ok?: boolean;
    message?: string;
    success?: string;
    error?: string;
    successCode?: string;
    errorCode?: string;
  }> {
    this.ensureInitialized();
    const body = new URLSearchParams();
    body.set('returnJson', '1');
    body.set('action', params.action);
    if (params.email !== undefined) body.set('email', params.email);
    if (params.fullname !== undefined) body.set('fullname', params.fullname);
    if (params.password !== undefined) body.set('password', params.password);
    if (params.send_email !== undefined) body.set('send_email', String(params.send_email));
    if (params.locale !== undefined && params.locale !== '') {
      body.set('locale', params.locale);
    }
    const response = await this.instance!.post(
      '/license-manager/action',
      body,
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      }
    );
    return response.data;
  }

  /**
   * License Manager: get offline computers for a user.
   */
  async getUserOfflineComputers(userId: string): Promise<{
    computers: { id: number; machine_name: string; key2: string; created_at: string }[];
    user: { id: number; fullname: string; email: string };
  }> {
    this.ensureInitialized();
    const response = await this.instance!.get(
      `/license-manager/offline-computers?userId=${encodeURIComponent(userId)}`
    );
    return response.data;
  }

  /**
   * License Manager: create offline computer (key1 + computerName + userId).
   */
  async createOfflineComputer(params: {
    userId: string;
    computerName: string;
    key1: string;
  }): Promise<{
    computers: { id: number; machine_name: string; key2: string; created_at: string }[];
    user: { id: number; fullname: string; email: string };
    newComputer?: { id: number; machine_name: string; key2: string };
    error?: string;
  }> {
    this.ensureInitialized();
    const response = await this.instance!.post(
      '/license-manager/offline-computers',
      {
        userId: params.userId,
        computerName: params.computerName,
        key1: params.key1.startsWith('%') ? params.key1 : encodeURIComponent(params.key1),
      },
      { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
    );
    return response.data;
  }

  /**
   * Exchange a browser login token for a session
   * This is called from the login page when a token is present
   * This is a public endpoint (no auth required)
   */
  async exchangeBrowserLoginToken(token: string): Promise<StateApiResponse> {
    // Use a temporary instance for token exchange (no auth required)
    const tempInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true, // Enable cookies for session
    });

    try {
      const response = await tempInstance.post<StateApiResponse>(
        '/web-api/browser-login-token/exchange',
        { token }
      );

      if (response.data) {
        return response.data;
      }

      return {
        ok: false,
        error: 'Invalid response from server'
      };
    } catch (error: any) {
      const errorData = error.response?.data;
      let errorMessage = 'Token exchange failed';
      
      if (errorData) {
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMessage = errorData.errors[0];
        }
      }
      
      return {
        ok: false,
        error: errorMessage
      };
    }
  }

  /**
   * Get all guides for the organization
   */
  async getGuides(): Promise<ApiResponse & { guides?: Guide[] }> {
    const response = await this.request<ApiResponse & { guides?: Guide[] }>({
      url: '/web-api/get-all-guides',
      method: 'POST'
    });
    return response.data;
  }

  /**
   * Get all articles for the organization
   */
  async getArticles(): Promise<ArticleApiResponse> {
    const response = await this.request<ArticleApiResponse>({
      url: '/web-api/articles/get-all',
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Get published articles for the organization
   */
  async getPublishedArticles(): Promise<ArticleApiResponse> {
    if (!this.instance) {
      throw new Error('API client not initialized');
    }

    try {
      const response = await this.instance.get<ArticleApiResponse>('/web-api/articles/published');
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch published articles';
      return {
        ok: false,
        error: errorMessage
      };
    }
  }

  /**
   * Import a document and convert it to TipTap JSON
   * Supports: .docx, .html, .md, .markdown, .txt
   */
  async importDocument(file: File): Promise<{
    ok: boolean;
    content?: any;
    html?: string;
    metadata?: {
      title?: string | null;
      description?: string | null;
      tags?: string[] | null;
      icon?: string | null;
      slug?: string | null;
    };
    error?: string;
  }> {
    if (!this.instance) {
      throw new Error('API client not initialized');
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await this.instance.post<{
        ok: boolean;
        content?: any;
        html?: string;
        metadata?: {
          title?: string | null;
          description?: string | null;
          tags?: string[] | null;
          icon?: string | null;
          slug?: string | null;
        };
        error?: string;
      }>('/web-api/articles/import-document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to import document';
      return {
        ok: false,
        error: errorMessage
      };
    }
  }

  
  /**
   * Get article by ID
   */
  // Article get/create/update/delete methods removed
  // Articles are now managed through help center items
  // Use getItem() instead to get article data

  /**
   * Get all help centers for the organization
   */
  async getHelpCenters(): Promise<HelpCentersApiResponse> {
    const response = await this.request<HelpCentersApiResponse>({
      url: '/web-api/help-centers',
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Get help center by ID
   */
  async getHelpCenter(helpCenterId: string): Promise<HelpCenterApiResponse> {
    const response = await this.request<HelpCenterApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Get help center by slug
   */
  async getHelpCenterBySlug(slug: string): Promise<HelpCenterApiResponse> {
    const response = await this.request<HelpCenterApiResponse>({
      url: `/web-api/help-centers/by-slug/${slug}`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Create new help center
   */
  async createHelpCenter(data: CreateHelpCenterRequest): Promise<HelpCenterApiResponse> {
    const response = await this.request<HelpCenterApiResponse>({
      url: '/web-api/help-centers',
      method: 'POST',
      data
    });
    return response.data;
  }

  /**
   * Update help center
   */
  async updateHelpCenter(helpCenterId: string, data: UpdateHelpCenterRequest): Promise<HelpCenterApiResponse> {
    const response = await this.request<HelpCenterApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}`,
      method: 'PUT',
      data
    });
    return response.data;
  }

  /**
   * Update help center settings (supports file uploads for logos)
   */
  async updateHelpCenterSettings(
    helpCenterId: string, 
    settings: Record<string, unknown>,
    logoFile?: File | null,
    removeFile?: boolean
  ): Promise<HelpCenterApiResponse> {
    const formData = new FormData();
    formData.append('settings', JSON.stringify(settings));
    
    if (removeFile) {
      formData.append('removeFile', 'true');
    }

    if (logoFile) {
      formData.append('file', logoFile);
    }

    const response = await this.request<HelpCenterApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/settings`,
      method: 'PATCH',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * Delete help center
   */
  async deleteHelpCenter(helpCenterId: string): Promise<ApiResponse> {
    const response = await this.request<ApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}`,
      method: 'DELETE'
    });
    return response.data;
  }

  /**
   * Get category tree for a help center
   */
  async getCategoryTree(helpCenterId: string): Promise<CategoriesApiResponse> {
    const response = await this.request<CategoriesApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/categories/tree`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Get all categories for a help center
   */
  async getCategories(helpCenterId: string): Promise<CategoriesApiResponse> {
    const response = await this.request<CategoriesApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/categories`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Create a new category
   */
  async createCategory(helpCenterId: string, data: CreateCategoryRequest): Promise<CategoryApiResponse> {
    const response = await this.request<CategoryApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/categories`,
      method: 'POST',
      data
    });
    return response.data;
  }

  /**
   * Update a category
   */
  async updateCategory(helpCenterId: string, categoryId: string, data: UpdateCategoryRequest): Promise<CategoryApiResponse> {
    const response = await this.request<CategoryApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/categories/${categoryId}`,
      method: 'PUT',
      data
    });
    return response.data;
  }

  /**
   * Delete a category
   */
  async deleteCategory(helpCenterId: string, categoryId: string): Promise<ApiResponse> {
    const response = await this.request<ApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/categories/${categoryId}`,
      method: 'DELETE'
    });
    return response.data;
  }

  /**
   * Reorder categories
   */
  async reorderCategories(helpCenterId: string, reorders: ReorderCategoriesRequest[]): Promise<ApiResponse> {
    const response = await this.request<ApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/categories/reorder`,
      method: 'POST',
      data: { reorders }
    });
    return response.data;
  }

  /**
   * Get complete structure (categories + items) for a help center
   */
  async getHelpCenterStructure(helpCenterId: string): Promise<ApiResponse & { structure?: HelpCenterStructureNode[] }> {
    const response = await this.request<ApiResponse & { structure?: HelpCenterStructureNode[] }>({
      url: `/web-api/help-centers/${helpCenterId}/structure`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Get all items for a help center
   */
  async getHelpCenterItems(helpCenterId: string): Promise<ItemsApiResponse> {
    const response = await this.request<ItemsApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/items`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Get a specific item
   */
  async getItem(helpCenterId: string, itemId: string): Promise<ItemApiResponse> {
    const response = await this.request<ItemApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/items/${itemId}`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Create a new item (add article/guide to help center)
   */
  async createItem(helpCenterId: string, data: CreateItemRequest): Promise<ItemApiResponse> {
    const response = await this.request<ItemApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/items`,
      method: 'POST',
      data
    });
    return response.data;
  }

  /**
   * Update an item
   */
  async updateItem(helpCenterId: string, itemId: string, data: UpdateItemRequest): Promise<ItemApiResponse> {
    const response = await this.request<ItemApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/items/${itemId}`,
      method: 'PUT',
      data
    });
    return response.data;
  }

  /**
   * Delete an item (remove article/guide from help center)
   */
  async deleteItem(helpCenterId: string, itemId: string): Promise<ApiResponse> {
    const response = await this.request<ApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/items/${itemId}`,
      method: 'DELETE'
    });
    return response.data;
  }

  /**
   * Reorder items
   */
  async reorderItems(helpCenterId: string, reorders: ReorderItemsRequest[]): Promise<ApiResponse> {
    const response = await this.request<ApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/items/reorder`,
      method: 'POST',
      data: { reorders }
    });
    return response.data;
  }

  /**
   * Publish an article item's draft → make it the live content (draft-mode articles).
   */
  async publishItem(helpCenterId: string, itemId: string): Promise<ItemApiResponse> {
    const response = await this.request<ItemApiResponse>({
      url: `/web-api/help-centers/${helpCenterId}/items/${itemId}/publish`,
      method: 'POST'
    });
    return response.data;
  }

  /**
   * Mint a short-lived token to preview an article item's draft through the portal.
   */
  async getArticlePreviewToken(helpCenterId: string, itemId: string): Promise<{ ok: boolean; token?: string; error?: string }> {
    const response = await this.request<{ ok: boolean; token?: string; error?: string }>({
      url: `/web-api/help-centers/${helpCenterId}/items/${itemId}/preview-token`,
      method: 'POST'
    });
    return response.data;
  }

  /**
   * List version history (revisions) for an article item. Metadata only; visibility gated by plan.
   */
  async getArticleRevisions(helpCenterId: string, itemId: string): Promise<RevisionsListResponse> {
    const response = await this.request<RevisionsListResponse>({
      url: `/web-api/help-centers/${helpCenterId}/items/${itemId}/revisions`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Get a single revision's full content (plan-gated; 403 => upgrade_required).
   */
  async getArticleRevision(helpCenterId: string, itemId: string, revisionId: string): Promise<RevisionResponse> {
    const response = await this.request<RevisionResponse>({
      url: `/web-api/help-centers/${helpCenterId}/items/${itemId}/revisions/${revisionId}`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Get feedback for a help center
   */
  async getHelpCenterFeedback(
    helpCenterId: string,
    filters?: {
      feedbackType?: string;
      rating?: number;
      userHash?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<ApiResponse & { feedback?: any[]; pagination?: any }> {
    const params: Record<string, string> = {};
    if (filters?.feedbackType) params.feedbackType = filters.feedbackType;
    if (filters?.rating !== undefined) params.rating = filters.rating.toString();
    if (filters?.userHash) params.userHash = filters.userHash;
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;
    if (filters?.page) params.page = filters.page.toString();
    if (filters?.limit) params.limit = filters.limit.toString();

    const response = await this.request<ApiResponse & { feedback?: any[]; pagination?: any }>({
      url: `/web-api/help-centers/${helpCenterId}/feedback`,
      method: 'GET',
      params
    });
    return response.data;
  }

  /**
   * Get feedback statistics for a help center
   */
  async getHelpCenterFeedbackStats(helpCenterId: string): Promise<ApiResponse & { stats?: any }> {
    const response = await this.request<ApiResponse & { stats?: any }>({
      url: `/web-api/help-centers/${helpCenterId}/feedback/stats`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Get all feedback for organization (optionally filtered by help center)
   */
  async getAllFeedback(
    filters?: {
      helpCenterId?: string;
      feedbackType?: string;
      rating?: number;
      userHash?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<ApiResponse & { feedback?: any[]; pagination?: any }> {
    const params: Record<string, string> = {};
    if (filters?.helpCenterId) params.helpCenterId = filters.helpCenterId;
    if (filters?.feedbackType) params.feedbackType = filters.feedbackType;
    if (filters?.rating !== undefined) params.rating = filters.rating.toString();
    if (filters?.userHash) params.userHash = filters.userHash;
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;
    if (filters?.page) params.page = filters.page.toString();
    if (filters?.limit) params.limit = filters.limit.toString();

    const response = await this.request<ApiResponse & { feedback?: any[]; pagination?: any }>({
      url: '/web-api/feedback',
      method: 'GET',
      params
    });
    return response.data;
  }

  /**
   * Get feedback statistics for organization (optionally filtered by help center)
   */
  async getAllFeedbackStats(helpCenterId?: string): Promise<ApiResponse & { stats?: any }> {
    const params: Record<string, string> = {};
    if (helpCenterId) params.helpCenterId = helpCenterId;

    const response = await this.request<ApiResponse & { stats?: any }>({
      url: '/web-api/feedback/stats',
      method: 'GET',
      params
    });
    return response.data;
  }

  /**
   * Get all assets for the organization
   */
  async getAllAssets(fileType?: string, limit?: number): Promise<AssetsApiResponse> {
    const params: Record<string, string> = {};
    if (fileType) params.file_type = fileType;
    if (limit) params.limit = limit.toString();
    
    const response = await this.request<AssetsApiResponse>({
      url: '/web-api/assets',
      method: 'GET',
      params
    });
    return response.data;
  }

  /**
   * Get all images for the organization
   */
  async getAllImages(limit?: number): Promise<AssetsApiResponse> {
    const params: Record<string, string> = {};
    if (limit) params.limit = limit.toString();
    
    const response = await this.request<AssetsApiResponse>({
      url: '/web-api/assets/images',
      method: 'GET',
      params
    });
    return response.data;
  }

  /**
   * Upload an asset file
   */
  async uploadAsset(file: File): Promise<AssetsApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.request<AssetsApiResponse>({
      url: '/web-api/assets/upload',
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * Create asset from URL
   * COMMENTED OUT: No longer creating asset records, just inserting URLs directly
   */
  // async createAssetFromUrl(data: CreateAssetFromUrlRequest): Promise<AssetsApiResponse> {
  //   const response = await this.request<AssetsApiResponse>({
  //     url: '/web-api/assets/from-url',
  //     method: 'POST',
  //     data
  //   });
  //   return response.data;
  // }

  /**
   * Delete an asset
   */
  async deleteAsset(assetId: string): Promise<ApiResponse> {
    const response = await this.request<ApiResponse>({
      url: `/web-api/assets/${assetId}`,
      method: 'DELETE'
    });
    return response.data;
  }

  /**
   * Handshake with server to authenticate and get user data
   */
  async handshake(token: string): Promise<{
    token: string;
    me: UserAccount;
    properties: UserProperties;
  }> {
    this.createApi(token);
    
    const stateResponse = await this.getState();
    
    if (!stateResponse.ok) {
      throw new Error('Invalid credentials or user does not exist');
    }

    if (!stateResponse.me || !stateResponse.properties) {
      throw new Error('Invalid response: missing user data or properties');
    }

    return {
      token: stateResponse.token || token,
      me: stateResponse.me,
      properties: stateResponse.properties
    };
  }

  /**
   * Clear API client state
   */
  clear(): void {
    this.instance = null;
    this.token = null;
    this.useCookies = false;
  }

  /**
   * Logout - clear session cookie
   */
  async logout(): Promise<void> {
    try {
      // Use a temporary instance for logout
      const tempInstance = axios.create({
        baseURL: API_BASE_URL,
        timeout: 30000,
        withCredentials: true,
      });
      
      await tempInstance.get('/logout');
      
      // Clear cookie
      if (typeof document !== 'undefined') {
        document.cookie = 'session-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      }
      
      // Clear localStorage (userStore.logout will handle this, but clear here too as backup)
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('folge-session-token');
          localStorage.removeItem('folge-user-account');
          localStorage.removeItem('folge-user-properties');
        }
      } catch (e) {
        console.error('Error clearing localStorage:', e);
      }
      
      this.clear();
    } catch (error) {
      console.error('Logout error:', error);
      // Clear anyway
      document.cookie = 'session-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      this.clear();
    }
  }

  // ============================================
  // Custom Domain Methods (paid plans only)
  // ============================================

  /**
   * Add a custom domain to a help center
   */
  async addCustomDomain(helpCenterId: string, domain: string): Promise<CustomDomainResponse> {
    const response = await this.request<CustomDomainResponse>({
      url: `/web-api/help-centers/${helpCenterId}/custom-domain`,
      method: 'POST',
      data: { domain }
    });
    return response.data;
  }

  /**
   * Remove custom domain from a help center
   */
  async removeCustomDomain(helpCenterId: string): Promise<ApiResponse & { customDomain: null }> {
    const response = await this.request<ApiResponse & { customDomain: null }>({
      url: `/web-api/help-centers/${helpCenterId}/custom-domain`,
      method: 'DELETE'
    });
    return response.data;
  }

  /**
   * Get custom domain status for a help center
   */
  async getCustomDomainStatus(helpCenterId: string): Promise<CustomDomainStatusResponse> {
    const response = await this.request<CustomDomainStatusResponse>({
      url: `/web-api/help-centers/${helpCenterId}/custom-domain`,
      method: 'GET'
    });
    return response.data;
  }

  /**
   * Verify custom domain DNS configuration
   */
  async verifyCustomDomain(helpCenterId: string): Promise<CustomDomainVerifyResponse> {
    const response = await this.request<CustomDomainVerifyResponse>({
      url: `/web-api/help-centers/${helpCenterId}/custom-domain/verify`,
      method: 'POST'
    });
    return response.data;
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Export individual functions for backward compatibility
export const createApi = (token: string) => apiClient.createApi(token);
export const getState = () => apiClient.getState();
export const getGuide = (guideId: string) => apiClient.getGuide(guideId);
export const deleteGuide = (guideId: string) => apiClient.deleteGuide(guideId);
export const updateGuideSettings = (guideId: string, settings: GuideSettingsUpdateRequest['settings']) => apiClient.updateGuideSettings(guideId, settings);
export const updatePortalSettings = (params: PortalUpdateRequest) => apiClient.updatePortalSettings(params);
export const getConfluenceSpaces = (connection: ConfluenceConnection) => apiClient.getConfluenceSpaces(connection);
export const generateBillingLink = () => apiClient.generateBillingLink();
export const generateBrowserLoginToken = () => apiClient.generateBrowserLoginToken();
export const exchangeBrowserLoginToken = (token: string) => apiClient.exchangeBrowserLoginToken(token);
export const handshake = (token: string) => apiClient.handshake(token);

// License Manager exports
export const getLicenseManagerDashboard = () => apiClient.getLicenseManagerDashboard();
export const licenseManagerLogout = () => apiClient.licenseManagerLogout();
export const licenseManagerAction = (params: Parameters<ApiClient['licenseManagerAction']>[0]) => apiClient.licenseManagerAction(params);
export const getUserOfflineComputers = (userId: string) => apiClient.getUserOfflineComputers(userId);
export const createOfflineComputer = (params: Parameters<ApiClient['createOfflineComputer']>[0]) => apiClient.createOfflineComputer(params);

// Guide exports
export const getGuides = () => apiClient.getGuides();

// Article exports
export const getArticles = () => apiClient.getArticles();
export const getPublishedArticles = () => apiClient.getPublishedArticles();
export const importDocument = (file: File) => apiClient.importDocument(file);
// Article get/create/update/delete exports removed - articles are now managed through help center items
// Use getItem() instead to get article data

// Help Center exports
export const getHelpCenters = () => apiClient.getHelpCenters();
export const getHelpCenter = (helpCenterId: string) => apiClient.getHelpCenter(helpCenterId);
export const getHelpCenterBySlug = (slug: string) => apiClient.getHelpCenterBySlug(slug);
export const createHelpCenter = (data: CreateHelpCenterRequest) => apiClient.createHelpCenter(data);
export const updateHelpCenter = (helpCenterId: string, data: UpdateHelpCenterRequest) => apiClient.updateHelpCenter(helpCenterId, data);
export const updateHelpCenterSettings = (
  helpCenterId: string, 
  settings: Record<string, unknown>,
  logoFile?: File | null,
  removeFile?: boolean
) => apiClient.updateHelpCenterSettings(helpCenterId, settings, logoFile, removeFile);
export const deleteHelpCenter = (helpCenterId: string) => apiClient.deleteHelpCenter(helpCenterId);

// Category exports
export const getCategoryTree = (helpCenterId: string) => apiClient.getCategoryTree(helpCenterId);
export const getCategories = (helpCenterId: string) => apiClient.getCategories(helpCenterId);
export const createCategory = (helpCenterId: string, data: CreateCategoryRequest) => apiClient.createCategory(helpCenterId, data);
export const updateCategory = (helpCenterId: string, categoryId: string, data: UpdateCategoryRequest) => apiClient.updateCategory(helpCenterId, categoryId, data);
export const deleteCategory = (helpCenterId: string, categoryId: string) => apiClient.deleteCategory(helpCenterId, categoryId);
export const reorderCategories = (helpCenterId: string, reorders: ReorderCategoriesRequest[]) => apiClient.reorderCategories(helpCenterId, reorders);

// Item exports
export const getHelpCenterStructure = (helpCenterId: string) => apiClient.getHelpCenterStructure(helpCenterId);
export const getHelpCenterItems = (helpCenterId: string) => apiClient.getHelpCenterItems(helpCenterId);
export const getItem = (helpCenterId: string, itemId: string) => apiClient.getItem(helpCenterId, itemId);
export const createItem = (helpCenterId: string, data: CreateItemRequest) => apiClient.createItem(helpCenterId, data);
export const updateItem = (helpCenterId: string, itemId: string, data: UpdateItemRequest) => apiClient.updateItem(helpCenterId, itemId, data);
export const deleteItem = (helpCenterId: string, itemId: string) => apiClient.deleteItem(helpCenterId, itemId);
export const reorderItems = (helpCenterId: string, reorders: ReorderItemsRequest[]) => apiClient.reorderItems(helpCenterId, reorders);

// Draft / Publish exports
export const publishItem = (helpCenterId: string, itemId: string) => apiClient.publishItem(helpCenterId, itemId);
export const getArticlePreviewToken = (helpCenterId: string, itemId: string) => apiClient.getArticlePreviewToken(helpCenterId, itemId);

// Version history exports
export const getArticleRevisions = (helpCenterId: string, itemId: string) => apiClient.getArticleRevisions(helpCenterId, itemId);
export const getArticleRevision = (helpCenterId: string, itemId: string, revisionId: string) => apiClient.getArticleRevision(helpCenterId, itemId, revisionId);

// Feedback exports
export const getHelpCenterFeedback = (helpCenterId: string, filters?: any) => apiClient.getHelpCenterFeedback(helpCenterId, filters);
export const getHelpCenterFeedbackStats = (helpCenterId: string) => apiClient.getHelpCenterFeedbackStats(helpCenterId);
export const getAllFeedback = (filters?: any) => apiClient.getAllFeedback(filters);
export const getAllFeedbackStats = (helpCenterId?: string) => apiClient.getAllFeedbackStats(helpCenterId);

// Custom Domain exports (paid plans only)
export const addCustomDomain = (helpCenterId: string, domain: string) => apiClient.addCustomDomain(helpCenterId, domain);
export const removeCustomDomain = (helpCenterId: string) => apiClient.removeCustomDomain(helpCenterId);
export const getCustomDomainStatus = (helpCenterId: string) => apiClient.getCustomDomainStatus(helpCenterId);
export const verifyCustomDomain = (helpCenterId: string) => apiClient.verifyCustomDomain(helpCenterId); 